import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import type { TelegramAuthData } from '@telegram-auth/react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { authClient, useSession } from '~/shared/lib/auth';

export const useTelegramLogin = (): UseMutationResult<unknown, { status?: number }, TelegramAuthData> => {
    const t = useTranslations();
    const searchParams = useSearchParams();
    const { refetch } = useSession();

    return useMutation({
        mutationFn: async payload => authClient.telegramAuth.login(payload),
        onSuccess(response) {
            if (response.data?.status) {
                refetch();

                const redirect = searchParams.get('redirect') ?? '/';
                setTimeout(() => {
                    window.location.replace(redirect);
                });
            }
        },
        onError(err) {
            const status = err?.status || 500;

            switch (status) {
                case 400:
                    toast.error(t('auth.errors.tgInvalidData'));
                    break;
                case 401:
                    toast.error(t('auth.errors.tgUnauthorized'));
                    break;
                case 403:
                    toast.error(t('auth.errors.tgForbidden'));
                    break;
                case 404:
                    toast.error(t('auth.errors.tgServiceMissing'));
                    break;
                case 500:
                    toast.error(t('auth.errors.tgServerError'));
                    break;
                default:
                    toast.error(t('auth.errors.tgFailed'));
            }
        },
    });
};
